import { useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import MasterEditModal from '../../components/MasterEditModal';
import { storageUrl } from '../../utils/urls';

export default function Manufacturers() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', logo: null });
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/manufacturers').then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const logoPreview = useMemo(() => {
    if (logoFile) return URL.createObjectURL(logoFile);
    return '';
  }, [logoFile]);

  useEffect(() => () => {
    if (logoPreview.startsWith('blob:')) URL.revokeObjectURL(logoPreview);
  }, [logoPreview]);

  const saveManufacturer = async (url, payloadValues, method = 'post') => {
    const payload = new FormData();
    payload.append('name', payloadValues.name);
    if (payloadValues.logo) payload.append('logo', payloadValues.logo);

    await api[method](url, payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const add = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await saveManufacturer('/manufacturers', { name: name.trim(), logo: logoFile });
      setName('');
      setLogoFile(null);
      setFileInputKey((current) => current + 1);
      load();
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({ name: row.name || '', logo: null });
  };

  const update = async () => {
    if (!editForm.name.trim() || !editRow) return;
    setSaving(true);
    try {
      await saveManufacturer(
        `/manufacturers/${editRow.id}`,
        { name: editForm.name.trim(), logo: editForm.logo },
        'put',
      );
      setEditRow(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm('تأكيد الحذف؟')) return;
    await api.delete(`/manufacturers/${row.id}`);
    load();
  };

  return (
    <div>
      <div className="mb-3 grid gap-2 md:grid-cols-[minmax(180px,1fr)_minmax(220px,1.3fr)_auto]">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded-lg border p-2"
          placeholder="اسم الشركة"
        />
        <label className="flex items-center gap-2 rounded-lg border bg-white p-2 text-sm text-slate-600">
          <span className="shrink-0">شعار الشركة</span>
          <input
            key={fileInputKey}
            type="file"
            accept="image/*"
            className="min-w-0 flex-1 text-sm file:ml-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-slate-700"
            onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
          />
        </label>
        <button
          onClick={add}
          disabled={saving}
          className="rounded-lg bg-primary px-3 py-2 text-white disabled:opacity-60"
        >
          إضافة
        </button>
      </div>

      {logoPreview && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-600">
          <img src={logoPreview} alt="معاينة شعار الشركة" className="h-14 w-14 rounded-lg border bg-white object-contain p-1" />
          <span>معاينة الشعار قبل الإضافة</span>
        </div>
      )}

      <table className="w-full overflow-hidden rounded-lg border text-sm">
        <thead className="bg-slate-100">
          <tr>
            <th className="p-2 text-right">الشعار</th>
            <th className="p-2 text-right">الاسم</th>
            <th className="p-2">إجراءات</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="w-20 p-2">
                {r.logo_path ? (
                  <img src={storageUrl(r.logo_path)} alt={r.name} className="h-11 w-11 rounded-lg border bg-white object-contain p-1" />
                ) : (
                  <span className="inline-grid h-11 w-11 place-items-center rounded-lg border bg-slate-50 text-xs text-slate-400">بدون</span>
                )}
              </td>
              <td className="p-2">{r.name}</td>
              <td className="flex justify-center gap-2 p-2">
                <button onClick={() => openEdit(r)} className="rounded border px-2 py-1">تعديل</button>
                <button onClick={() => remove(r)} className="rounded border px-2 py-1 text-red-600">حذف</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <MasterEditModal
        open={Boolean(editRow)}
        title="تعديل الشركة"
        subtitle="عدّل اسم الشركة أو اختر شعاراً جديداً ثم اضغط حفظ."
        fields={[
          { name: 'name', label: 'اسم الشركة', required: true, fullWidth: true },
          { name: 'logo', label: 'شعار الشركة', type: 'file', accept: 'image/*', fullWidth: true },
        ]}
        values={editForm}
        onChange={(key, value) => setEditForm((current) => ({ ...current, [key]: value }))}
        onClose={() => setEditRow(null)}
        onSubmit={update}
        saving={saving}
      />
    </div>
  );
}
